import { useCallback, useEffect, useState } from "react";
import {
  ChartBar,
  ChatCircleDots,
  Clock,
  EnvelopeSimple,
  Gauge,
  Phone,
  SpinnerGap,
  UserCircle,
  Users,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import { api } from "@/lib/api";

const metricCards = [
  { key: "total_users", label: "Total users", icon: Users },
  { key: "tester_access_records", label: "Authorized testers", icon: UserCircle },
  { key: "beta_requests_pending", label: "Pending requests", icon: EnvelopeSimple },
  { key: "total_calls", label: "Total calls", icon: Phone },
  { key: "calls_last_7_days", label: "Calls, last 7 days", icon: Clock },
  { key: "feedback_new", label: "New feedback", icon: ChatCircleDots },
];

function formatDate(value) {
  if (!value) return "—";
  return new Date(value).toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatQuota(used, limit) {
  if (limit === null || limit === undefined) return `${used ?? 0} used`;
  return `${used ?? 0}/${limit}`;
}

function FeedbackCard({ item, onStatusChange, busy }) {
  return (
    <article className="admin-feedback-card">
      <div className="admin-feedback-card__topline">
        <div>
          <span className={`admin-feedback-category is-${item.category}`}>{item.category}</span>
          <strong>{item.user_name || item.user_email || "Beta tester"}</strong>
        </div>
        <time>{formatDate(item.created_at)}</time>
      </div>
      <p>{item.message}</p>
      <div className="admin-feedback-card__meta">
        <span>{item.user_email || "Email unavailable"}</span>
        <span>{item.page_path || "Page unavailable"}</span>
        <span>{item.rating ? `${item.rating}/5 rating` : "No rating"}</span>
        <label>
          <span className="sr-only">Feedback status</span>
          <select
            value={item.status}
            onChange={(event) => onStatusChange(item.id, event.target.value)}
            disabled={busy}
          >
            <option value="new">New</option>
            <option value="reviewed">Reviewed</option>
            <option value="resolved">Resolved</option>
          </select>
        </label>
      </div>
    </article>
  );
}

export default function AdminPage() {
  const [overview, setOverview] = useState({ metrics: {}, feedback: [] });
  const [testers, setTesters] = useState([]);
  const [usage, setUsage] = useState({ global: {}, limits: {}, usage_note: "", cost_tracking: {} });
  const [betaRequests, setBetaRequests] = useState([]);
  const [testerEmail, setTesterEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [testerAction, setTesterAction] = useState(null);

  const loadAdminData = useCallback(async () => {
    try {
      const [overviewResponse, testersResponse, usageResponse, requestsResponse] = await Promise.all([
        api.get("/admin/overview"),
        api.get("/admin/testers"),
        api.get("/admin/beta-usage"),
        api.get("/admin/beta-requests"),
      ]);
      const testerRows = testersResponse.data.testers || [];
      setOverview({
        ...overviewResponse.data,
        metrics: {
          ...(overviewResponse.data.metrics || {}),
          tester_access_records: testerRows.length,
        },
      });
      setTesters(testerRows);
      setUsage(usageResponse.data || { global: {}, limits: {} });
      setBetaRequests(requestsResponse.data.requests || []);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Admin data could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAdminData();
  }, [loadAdminData]);

  const updateStatus = async (id, nextStatus) => {
    setUpdatingId(id);
    try {
      const { data } = await api.patch(`/admin/feedback/${id}`, { status: nextStatus });
      setOverview((current) => ({
        ...current,
        metrics: {
          ...current.metrics,
          feedback_new:
            current.feedback.find((item) => item.id === id)?.status === "new" && nextStatus !== "new"
              ? Math.max(0, (current.metrics.feedback_new || 0) - 1)
              : current.feedback.find((item) => item.id === id)?.status !== "new" && nextStatus === "new"
                ? (current.metrics.feedback_new || 0) + 1
                : current.metrics.feedback_new,
        },
        feedback: current.feedback.map((item) =>
          item.id === id ? { ...item, status: data.status, updated_at: data.updated_at } : item
        ),
      }));
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Status could not be updated.");
    } finally {
      setUpdatingId(null);
    }
  };

  const authorizeTesterEmail = async (rawEmail) => {
    const email = rawEmail.trim().toLowerCase();
    if (!email) return;
    setTesterAction(email);
    try {
      const { data } = await api.post("/admin/testers", { email });
      if (data.email_sent === false && data.invite_status === "failed") {
        toast.warning(data.message || "Tester authorized, but invitation delivery failed.");
      } else {
        toast.success(data.message || "Tester authorized and invited.");
      }
      setTesterEmail("");
      await loadAdminData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Tester could not be authorized.");
    } finally {
      setTesterAction(null);
    }
  };

  const authorizeTester = async (event) => {
    event.preventDefault();
    await authorizeTesterEmail(testerEmail);
  };

  const resendInvitation = async (tester) => {
    setTesterAction(tester.email);
    try {
      const { data } = await api.post(
        `/admin/testers/${encodeURIComponent(tester.email)}/resend-invite`
      );
      if (data.email_sent === false && data.invite_status === "failed") {
        toast.warning(data.message || "The tester remains authorized, but delivery failed.");
      } else {
        toast.success(data.message || "Invitation sent again.");
      }
      await loadAdminData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Invitation could not be resent.");
    } finally {
      setTesterAction(null);
    }
  };

  const updateTesterStatus = async (tester) => {
    const nextStatus = tester.status === "suspended" ? "authorized" : "suspended";
    setTesterAction(tester.email);
    try {
      const { data } = await api.patch(
        `/admin/testers/${encodeURIComponent(tester.email)}`,
        { status: nextStatus }
      );
      toast.success(data.message || "Tester access updated.");
      await loadAdminData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Tester access could not be updated.");
    } finally {
      setTesterAction(null);
    }
  };

  const pendingRequests = betaRequests.filter((item) => item.status === "pending");

  return (
    <div className="admin-page">
      <Nav />
      <main className="admin-shell">
        <header className="admin-header">
          <div>
            <div className="dashboard-section-label">Owner workspace</div>
            <h1>Beta overview</h1>
            <p>Operational access, allowance counters and feedback, without call or transcript content.</p>
          </div>
        </header>

        {loading ? (
          <div className="admin-loading" role="status">
            <SpinnerGap size={24} className="animate-spin" />
            Loading beta overview…
          </div>
        ) : (
          <>
            <section className="admin-metrics" aria-label="Beta metrics">
              {metricCards.map(({ key, label, icon: Icon }) => (
                <article className="admin-metric-card" key={key}>
                  <span aria-hidden="true"><Icon size={19} weight="duotone" /></span>
                  <div>
                    <small>{label}</small>
                    <strong>{overview.metrics[key] ?? 0}</strong>
                  </div>
                </article>
              ))}
            </section>

            <section className="admin-testers admin-usage">
              <div className="admin-section-heading admin-testers__heading">
                <div>
                  <div className="dashboard-section-label">Private beta</div>
                  <h2>Beta Usage</h2>
                  <p>Technical allowance counters and deployment limits. These are not provider billing figures.</p>
                </div>
                <button type="button" onClick={loadAdminData}>Refresh</button>
              </div>

              <div className="admin-usage-grid">
                <UsageCard
                  icon={Phone}
                  label="Global calls"
                  used={usage.global.calls_used}
                  limit={usage.limits.calls_global}
                  remaining={usage.global.calls_remaining}
                />
                <UsageCard
                  icon={ChartBar}
                  label="Global generations"
                  used={usage.global.generations_used}
                  limit={usage.limits.generations_global}
                  remaining={usage.global.generations_remaining}
                />
                <UsageCard
                  icon={Gauge}
                  label="Per tester"
                  value={`${usage.limits.calls_per_tester ?? "—"} calls`}
                  detail={`${usage.limits.generations_per_call_and_type ?? "—"} generations per call/type`}
                />
                <UsageCard
                  icon={Clock}
                  label="Upload limits"
                  value={`${usage.limits.max_audio_minutes ?? "—"} min`}
                  detail={`${usage.limits.max_upload_mb ?? "—"} MB maximum file size`}
                />
              </div>

              <div className="admin-usage-note">
                <strong>Allowance counters, not OpenAI cost.</strong>
                <span>{usage.usage_note}</span>
                <span>{usage.cost_tracking?.message}</span>
              </div>
            </section>

            <section className="admin-testers">
              <div className="admin-section-heading admin-testers__heading">
                <div>
                  <div className="dashboard-section-label">Public landing page</div>
                  <h2>Beta access requests</h2>
                  <p>Requests remain pending until you explicitly authorize and invite the applicant.</p>
                </div>
                <button type="button" onClick={loadAdminData}>Refresh</button>
              </div>

              {pendingRequests.length ? (
                <div className="admin-request-list">
                  {pendingRequests.map((request) => {
                    const busy = testerAction === request.email;
                    return (
                      <article className="admin-request-card" key={request.id || request.email}>
                        <div className="admin-request-card__header">
                          <div>
                            <strong>{request.name}</strong>
                            <span>{request.email}</span>
                          </div>
                          <span className="admin-request-status">Pending</span>
                        </div>
                        <div className="admin-request-card__context">
                          <span>{request.company || "Company not provided"}</span>
                          <span>{request.role || "Role not provided"}</span>
                          <span>Received {formatDate(request.created_at)}</span>
                        </div>
                        <p>{request.use_case}</p>
                        <button
                          type="button"
                          onClick={() => authorizeTesterEmail(request.email)}
                          disabled={Boolean(testerAction)}
                        >
                          {busy ? <SpinnerGap size={16} className="animate-spin" /> : null}
                          Authorize &amp; invite
                        </button>
                      </article>
                    );
                  })}
                </div>
              ) : (
                <div className="admin-empty-state admin-empty-state--compact">
                  <EnvelopeSimple size={28} weight="duotone" />
                  <h3>No pending access requests</h3>
                  <p>New landing-page requests will appear here.</p>
                </div>
              )}
            </section>

            <section className="admin-testers">
              <div className="admin-section-heading admin-testers__heading">
                <div>
                  <div className="dashboard-section-label">Private beta</div>
                  <h2>Tester access and usage</h2>
                  <p>Authorize, invite, monitor usage and suspend access without changing Render settings.</p>
                </div>
                <button type="button" onClick={loadAdminData}>Refresh</button>
              </div>

              <form className="admin-tester-form" onSubmit={authorizeTester}>
                <label htmlFor="admin-tester-email">Authorize and invite a tester</label>
                <div>
                  <input
                    id="admin-tester-email"
                    type="email"
                    autoComplete="email"
                    placeholder="tester@company.com"
                    value={testerEmail}
                    onChange={(event) => setTesterEmail(event.target.value)}
                    disabled={Boolean(testerAction)}
                    required
                  />
                  <button
                    type="submit"
                    aria-label="Authorize tester and send invitation"
                    disabled={Boolean(testerAction) || !testerEmail.trim()}
                  >
                    {testerAction === testerEmail.trim().toLowerCase() ? (
                      <SpinnerGap size={17} className="animate-spin" />
                    ) : null}
                    Authorize &amp; invite
                  </button>
                </div>
                <small>
                  Authorization is saved before email delivery. A failed invitation never revokes tester access.
                </small>
              </form>

              {testers.length ? (
                <div className="admin-tester-list">
                  {testers.map((tester) => {
                    const busy = testerAction === tester.email;
                    return (
                      <article className="admin-tester-card" key={tester.email}>
                        <div className="admin-tester-card__identity">
                          <div>
                            <strong>{tester.name || tester.email.split("@")[0]}</strong>
                            <span>{tester.email}</span>
                          </div>
                          <span className={`admin-tester-status is-${tester.status}`}>
                            {tester.status}
                          </span>
                        </div>
                        <div className="admin-tester-card__meta">
                          <span>
                            Calls: {formatQuota(tester.calls_used, tester.calls_limit)} · {tester.calls_remaining ?? "—"} remaining
                          </span>
                          <span>
                            Generations: {tester.generations_used ?? 0} used · {tester.generations_remaining_tracked ?? 0} remaining in tracked call/type slots
                          </span>
                          <span>
                            Generation cap: {tester.generations_per_call_and_type ?? "—"} per call/type; new call/type slots add their own allowance
                          </span>
                          <span>
                            Sandbox: {tester.sandbox_transcriptions_used ?? 0} transcriptions · {tester.sandbox_generations_used ?? 0} generations
                          </span>
                          <span>
                            Saved calls: {tester.saved_calls ?? 0} · Last activity: {formatDate(tester.last_activity_at)}
                          </span>
                          <span>
                            {tester.registered
                              ? `Registered ${formatDate(tester.account_created_at)}`
                              : "Not registered yet"}
                          </span>
                          <span>
                            Invitation: {tester.invite_status === "accepted"
                              ? "Accepted"
                              : tester.invite_status === "sent"
                                ? `Sent ${formatDate(tester.invite_sent_at)}`
                                : tester.invite_status === "failed"
                                  ? "Delivery failed"
                                  : "Not sent"}
                          </span>
                        </div>
                        <div className="admin-tester-card__actions">
                          {!tester.registered && tester.status !== "suspended" ? (
                            <button
                              type="button"
                              className="is-resend"
                              onClick={() => resendInvitation(tester)}
                              disabled={busy || Boolean(testerAction)}
                            >
                              {busy ? <SpinnerGap size={16} className="animate-spin" /> : null}
                              Resend invitation
                            </button>
                          ) : null}
                          <button
                            type="button"
                            className={tester.status === "suspended" ? "is-reactivate" : "is-suspend"}
                            onClick={() => updateTesterStatus(tester)}
                            disabled={busy || Boolean(testerAction)}
                          >
                            {busy ? <SpinnerGap size={16} className="animate-spin" /> : null}
                            {tester.status === "suspended" ? "Reactivate" : "Suspend"}
                          </button>
                        </div>
                      </article>
                    );
                  })}
                </div>
              ) : (
                <div className="admin-empty-state admin-empty-state--compact">
                  <UserCircle size={28} weight="duotone" />
                  <h3>No testers authorized yet</h3>
                  <p>Add the first email above.</p>
                </div>
              )}
            </section>

            <section className="admin-feedback">
              <div className="admin-section-heading">
                <div>
                  <div className="dashboard-section-label">Private beta</div>
                  <h2>Tester feedback</h2>
                </div>
                <button type="button" onClick={loadAdminData}>Refresh</button>
              </div>

              {overview.feedback.length ? (
                <div className="admin-feedback-list">
                  {overview.feedback.map((item) => (
                    <FeedbackCard
                      key={item.id}
                      item={item}
                      busy={updatingId === item.id}
                      onStatusChange={updateStatus}
                    />
                  ))}
                </div>
              ) : (
                <div className="admin-empty-state">
                  <ChatCircleDots size={30} weight="duotone" />
                  <h3>No feedback yet</h3>
                  <p>Submissions from the in-app feedback button will appear here.</p>
                </div>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
}

function UsageCard({ icon: Icon, label, used, limit, remaining, value, detail }) {
  return (
    <article className="admin-usage-card">
      <span><Icon size={19} weight="duotone" /></span>
      <div>
        <small>{label}</small>
        <strong>{value || formatQuota(used, limit)}</strong>
        <em>{detail || `${remaining ?? "—"} remaining`}</em>
      </div>
    </article>
  );
}
